import type { DevicePreset } from '@busswerks/shared'
import type { Device, Port } from '@/types/devices'

/**
 * Decide whether a preset/port represents a physically bidirectional
 * connector. Honours an explicit `bidirectional: true` flag, and also
 * auto-detects USB: USB is a bidirectional bus, so any port whose
 * signalType is `usb` or whose connector is USB-A/B/C is treated as
 * bidirectional — the explicit flag is no longer required for USB.
 */
export function isBidirectionalPort(port: {
  signalType?: string
  connectorFormat?: string
  bidirectional?: boolean
}): boolean {
  if (port.bidirectional) return true
  if (port.signalType === 'usb') return true
  if (port.connectorFormat && port.connectorFormat.startsWith('USB'))
    return true
  return false
}

/**
 * Backfill bidirectional twin ports on a Device that was persisted
 * before the USB-is-bidirectional normalization was in place. For
 * every port that `isBidirectionalPort` considers bidirectional but
 * is missing its `bidirectionalPairId`, we stamp a deterministic pair
 * id on the existing port and synthesize a twin on the opposite side
 * (unless a matching twin already exists).
 *
 * Twin IDs are deterministic (`${primary.id}-bi-twin`) so hydrating
 * the same persisted device repeatedly always yields the same twin
 * id — connections that reference twin ports survive reloads.
 *
 * Returns the original Device object unchanged when nothing needed
 * fixing, so `devices.map(normalizeDevicePorts)` is cheap on
 * already-correct data.
 */
export function normalizeDevicePorts(device: Device): Device {
  const inputs: Port[] = device.inputs.map(p => ({ ...p }))
  const outputs: Port[] = device.outputs.map(p => ({ ...p }))
  let changed = false

  // If an unpaired bidirectional port already has a matching unpaired
  // counterpart on the other side (same signalType + connectorFormat),
  // pair them up instead of synthesizing a twin — this handles devices
  // whose original preset listed USB in/out as separate entries.
  const matchExistingCounterpart = (
    port: Port,
    otherSide: Port[],
  ): Port | undefined => {
    return otherSide.find(
      p =>
        !p.bidirectionalPairId &&
        isBidirectionalPort(p) &&
        p.signalType === port.signalType &&
        (p.connectorFormat ?? null) === (port.connectorFormat ?? null),
    )
  }

  const backfillSide = (side: Port[], otherSide: Port[]) => {
    for (let i = 0; i < side.length; i++) {
      const port = side[i]
      if (port.bidirectionalPairId) continue
      if (!isBidirectionalPort(port)) continue

      const pairId = `bi-${port.id}`
      const existingCounterpart = matchExistingCounterpart(port, otherSide)

      side[i] = {
        ...port,
        bidirectional: true,
        bidirectionalPairId: pairId,
      }
      changed = true

      if (existingCounterpart) {
        // Pair up the existing counterpart instead of adding a twin.
        const idx = otherSide.indexOf(existingCounterpart)
        otherSide[idx] = {
          ...existingCounterpart,
          bidirectional: true,
          bidirectionalPairId: pairId,
        }
      } else {
        otherSide.push({
          ...port,
          id: `${port.id}-bi-twin`,
          bidirectional: true,
          bidirectionalPairId: pairId,
        })
      }
    }
  }

  backfillSide(inputs, outputs)
  backfillSide(outputs, inputs)

  if (!changed) return device
  return { ...device, inputs, outputs }
}

/**
 * Expand a preset's inputs/outputs into concrete Device ports,
 * splitting each bidirectional connector (explicit flag or
 * auto-detected USB) into twin Port records — one on `inputs`, one
 * on `outputs` — that share a `bidirectionalPairId` so the UI knows
 * they represent a single physical connector. `nextId` supplies port
 * IDs so callers can plug in their own generator (e.g. deterministic
 * counter for seed data, random for runtime).
 */
export function expandPresetPorts(
  preset: { inputs: DevicePreset['inputs']; outputs: DevicePreset['outputs'] },
  nextId: () => string,
): { inputs: Port[]; outputs: Port[] } {
  const inputs: Port[] = []
  const outputs: Port[] = []

  for (const port of preset.inputs) {
    const primaryId = nextId()
    if (isBidirectionalPort(port)) {
      const pairId = `bi-${primaryId}`
      inputs.push({
        ...port,
        id: primaryId,
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
      outputs.push({
        ...port,
        id: nextId(),
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
    } else {
      inputs.push({ ...port, id: primaryId })
    }
  }
  for (const port of preset.outputs) {
    const primaryId = nextId()
    if (isBidirectionalPort(port)) {
      const pairId = `bi-${primaryId}`
      outputs.push({
        ...port,
        id: primaryId,
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
      inputs.push({
        ...port,
        id: nextId(),
        bidirectional: true,
        bidirectionalPairId: pairId,
      })
    } else {
      outputs.push({ ...port, id: primaryId })
    }
  }

  return { inputs, outputs }
}

/**
 * Convert a `DevicePreset` into a concrete `Device` ready to drop
 * onto the canvas. Handles the bidirectional-port expansion: a
 * preset port flagged `bidirectional: true` (or auto-detected as USB)
 * becomes twin Port records — one on `inputs`, one on `outputs` —
 * sharing a `bidirectionalPairId` so the rest of the app can detect
 * they represent the same physical connector.
 *
 * Used by both AddDeviceDialog (manual browse) and
 * PhotoAddDeviceDialog (Claude vision identify flow) so the two
 * entry points produce identical devices.
 */
export function presetToDevice(
  preset: DevicePreset,
  opts: {
    position: { x: number; y: number }
    zoneId?: string
  },
): Device {
  const id = `device-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
  const makePortId = () =>
    `port-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`

  const { inputs, outputs } = expandPresetPorts(preset, makePortId)

  return {
    id,
    name: preset.name,
    manufacturer: preset.manufacturer,
    category: preset.category,
    inputs,
    outputs,
    position: opts.position,
    zoneId: opts.zoneId,
  }
}

/**
 * Staggered grid position for a freshly-added device. `index` is
 * typically `devices.length + addedThisSession` so successive adds
 * don't pile on top of each other.
 */
export function gridPosition(index: number): { x: number; y: number } {
  const row = Math.floor(index / 4)
  const col = index % 4
  return { x: col * 250 + 50, y: row * 250 + 50 }
}
